import { useEffect, useMemo, useState } from "react";

import type {
  ConversationMessageDto,
  QaChunk,
} from "@oracus/shared/api/qa";

import {
  useConversation,
  useConversations,
  useDeleteConversation,
  useQaStream,
} from "../api/qa.api.ts";
import { useRepos } from "../api/repos.api.ts";
import { useTeams } from "../api/teams.api.ts";
import { MarkdownAnswer } from "../components/MarkdownAnswer.tsx";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

type Citation = Extract<QaChunk, { type: "citation" }>;

interface PendingTurn {
  question: string;
  answer: string;
  citations: Citation[];
  isStreaming: boolean;
  error: string | null;
}

const EMPTY_PENDING: PendingTurn = {
  question: "",
  answer: "",
  citations: [],
  isStreaming: false,
  error: null,
};

export function Chat() {
  const conversations = useConversations();
  const [activeId, setActiveId] = useState<string | null>(null);
  const detail = useConversation(activeId);
  const { chunks, isStreaming, error, conversationId, ask, reset } =
    useQaStream();
  const [question, setQuestion] = useState("");

  // When a stream resolves to a new conversation, switch to it.
  useEffect(() => {
    if (conversationId && conversationId !== activeId) {
      setActiveId(conversationId);
    }
  }, [conversationId, activeId]);

  // Rebuild the in-flight turn from streaming chunks.
  const pending: PendingTurn = useMemo(() => {
    if (chunks.length === 0 && !isStreaming && !error) return EMPTY_PENDING;
    const tokens = chunks.filter((c) => c.type === "token");
    const citations = chunks.filter(
      (c): c is Citation => c.type === "citation",
    );
    const answer = tokens
      .map((t) => (t.type === "token" ? t.text : ""))
      .join("");
    return {
      question: "", // owned by the user submission, separate from chunks
      answer,
      citations,
      isStreaming,
      error,
    };
  }, [chunks, isStreaming, error]);

  // The persisted history (everything before whatever is currently streaming).
  const history: ConversationMessageDto[] = detail.data?.messages ?? [];

  // While streaming, we want to show the user's just-asked question and the
  // assistant's incremental answer. After `done`, the persisted history
  // refresh from the server — at which point the streaming state is already
  // empty (chunks reset on next ask).
  const [optimisticUserMsg, setOptimisticUserMsg] = useState<string | null>(
    null,
  );
  // Scope picker: "all" (default), or `repo:<uuid>` / `team:<uuid>`.
  // Encodes both kinds in one select for a compact UI.
  const [scopeValue, setScopeValue] = useState<string>("all");
  const repos = useRepos();
  const teams = useTeams();

  useEffect(() => {
    if (!isStreaming && pending.answer === "") {
      setOptimisticUserMsg(null);
    }
  }, [isStreaming, pending.answer]);

  const handleAsk = () => {
    const trimmed = question.trim();
    if (!trimmed || isStreaming) return;
    setOptimisticUserMsg(trimmed);
    const scope = parseScopeValue(scopeValue);
    ask({
      question: trimmed,
      ...(activeId ? { conversationId: activeId } : {}),
      ...(scope ? { scope } : {}),
    });
    setQuestion("");
  };

  const handleNewChat = () => {
    setActiveId(null);
    reset();
    setOptimisticUserMsg(null);
    setQuestion("");
  };

  const deleteConversation = useDeleteConversation();
  const handleDelete = (id: string) => {
    const title =
      conversations.data?.conversations.find((c) => c.id === id)?.title ?? "";
    if (!window.confirm(`Delete conversation "${title}"? This can't be undone.`)) {
      return;
    }
    deleteConversation.mutate(id, {
      onSuccess: () => {
        if (id === activeId) {
          setActiveId(null);
          reset();
          setOptimisticUserMsg(null);
        }
      },
    });
  };

  return (
    <Page
      title="Ask"
      description="Multi-turn natural-language Q&A grounded in your tickets, tests, repo specs, and docs."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        <ConversationSidebar
          activeId={activeId}
          conversations={conversations.data?.conversations ?? []}
          onSelect={(id) => {
            setActiveId(id);
            reset();
            setOptimisticUserMsg(null);
          }}
          onNew={handleNewChat}
          onDelete={handleDelete}
          deletingId={
            deleteConversation.isPending
              ? (deleteConversation.variables ?? null)
              : null
          }
        />

        <Card>
          <div className="flex flex-col gap-4">
            <MessageHistory
              history={history}
              optimisticUserMsg={optimisticUserMsg}
              pending={pending}
              isLoading={detail.isLoading && activeId !== null}
            />

            <div className="border-t border-zinc-100 pt-3">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleAsk();
                  }
                }}
                placeholder={
                  activeId
                    ? "Follow-up… (⌘/Ctrl+Enter to send)"
                    : "What does the checkout module do, and what's actually tested? (⌘/Ctrl+Enter to send)"
                }
                className="block h-20 w-full rounded-md border border-zinc-300 bg-white p-3 text-sm focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="hidden sm:inline">Scope</span>
                  <select
                    value={scopeValue}
                    onChange={(e) => setScopeValue(e.target.value)}
                    className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs"
                    title="Limit retrieval to a repo or team"
                  >
                    <option value="all">All repos</option>
                    {(repos.data?.repos ?? []).length > 0 && (
                      <optgroup label="Repo">
                        {repos.data?.repos.map((r) => (
                          <option key={r.id} value={`repo:${r.id}`}>
                            {r.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {(teams.data?.teams ?? []).length > 0 && (
                      <optgroup label="Team">
                        {teams.data?.teams.map((t) => (
                          <option key={t.id} value={`team:${t.id}`}>
                            {t.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <span className="hidden sm:inline">·</span>
                  <span className="truncate">
                    {activeId
                      ? `${detail.data?.conversation.title ?? "loading…"}`
                      : "New conversation"}
                  </span>
                </div>
                <Button
                  onClick={handleAsk}
                  disabled={!question.trim() || isStreaming}
                >
                  {isStreaming ? "Streaming…" : "Send"}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}

function parseScopeValue(
  value: string,
): { repoIds?: string[]; teamId?: string } | null {
  if (value === "all") return null;
  if (value.startsWith("repo:")) {
    return { repoIds: [value.slice("repo:".length)] };
  }
  if (value.startsWith("team:")) {
    return { teamId: value.slice("team:".length) };
  }
  return null;
}

function ConversationSidebar({
  activeId,
  conversations,
  onSelect,
  onNew,
  onDelete,
  deletingId,
}: {
  activeId: string | null;
  conversations: { id: string; title: string; updatedAt: string }[];
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
}) {
  return (
    <aside className="rounded-lg border border-zinc-200 bg-white p-3 shadow-sm">
      <button
        type="button"
        onClick={onNew}
        className={`mb-2 w-full rounded-md px-3 py-2 text-left text-sm font-medium transition ${
          activeId === null
            ? "bg-zinc-900 text-white"
            : "border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50"
        }`}
      >
        + New chat
      </button>
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
        Recent
      </div>
      <ul className="mt-2 max-h-[60vh] space-y-1 overflow-y-auto">
        {conversations.length === 0 && (
          <li className="px-2 py-1.5 text-xs text-zinc-500">
            No conversations yet.
          </li>
        )}
        {conversations.map((c) => {
          const isActive = c.id === activeId;
          const isDeleting = c.id === deletingId;
          return (
            <li key={c.id} className="group relative">
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                disabled={isDeleting}
                className={`block w-full truncate rounded px-2 py-1.5 pr-7 text-left text-sm transition disabled:opacity-50 ${
                  isActive
                    ? "bg-zinc-100 text-zinc-900"
                    : "text-zinc-700 hover:bg-zinc-50"
                }`}
                title={c.title}
              >
                {c.title}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(c.id);
                }}
                disabled={isDeleting}
                aria-label={`Delete conversation ${c.title}`}
                title="Delete conversation"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 opacity-0 transition hover:bg-zinc-200 hover:text-zinc-800 focus:opacity-100 group-hover:opacity-100 disabled:opacity-50"
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function MessageHistory({
  history,
  optimisticUserMsg,
  pending,
  isLoading,
}: {
  history: ConversationMessageDto[];
  optimisticUserMsg: string | null;
  pending: PendingTurn;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-zinc-500">Loading conversation…</p>;
  }

  // Some history may include the just-asked optimistic message; de-dupe so
  // we don't show it twice while waiting for the persisted refresh to land.
  const lastHistoryUser = [...history].reverse().find((m) => m.role === "user");
  const showOptimistic =
    optimisticUserMsg !== null && lastHistoryUser?.content !== optimisticUserMsg;

  const showPendingAssistant =
    pending.isStreaming || pending.answer.length > 0 || pending.error !== null;

  // Once the stream completes and the persisted history catches up to
  // include the assistant message we just streamed, suppress the in-flight
  // copy to avoid showing both. We compare on (trimmed) content equality —
  // history is the source of truth once it lands.
  const lastHistoryAssistant = [...history]
    .reverse()
    .find((m) => m.role === "assistant");
  const pendingAlreadyPersisted =
    !pending.isStreaming &&
    pending.answer.length > 0 &&
    lastHistoryAssistant?.content.trim() === pending.answer.trim();

  const renderPendingAssistant = showPendingAssistant && !pendingAlreadyPersisted;

  if (history.length === 0 && !showOptimistic && !renderPendingAssistant) {
    return (
      <p className="text-sm text-zinc-500">
        Ask anything about your tickets, tests, repo specs, and docs. Every
        claim is cited.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {history.map((m) => (
        <Turn
          key={m.id}
          role={m.role}
          content={m.content}
          citations={m.citations}
        />
      ))}
      {showOptimistic && (
        <Turn role="user" content={optimisticUserMsg!} citations={[]} />
      )}
      {renderPendingAssistant && (
        <Turn
          role="assistant"
          content={pending.answer}
          citations={pending.citations.map((c) => ({
            sourceType: c.sourceType,
            sourceId: c.sourceId,
            label: c.label,
          }))}
          isStreaming={pending.isStreaming}
          error={pending.error}
        />
      )}
    </div>
  );
}

const CITATION_PREVIEW_COUNT = 5;

function CitationStrip({
  citations,
}: {
  citations: { sourceType: string; sourceId: string; label: string }[];
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded
    ? citations
    : citations.slice(0, CITATION_PREVIEW_COUNT);
  const hidden = citations.length - visible.length;

  return (
    <div className="mb-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {visible.map((c, idx) => {
          const tone =
            c.sourceType === "ticket"
              ? "info"
              : c.sourceType === "test_case"
                ? "success"
                : c.sourceType === "repo_spec"
                  ? "warn"
                  : c.sourceType === "repo"
                    ? "info"
                    : "neutral";
          // Repo citations link to the Service map with the slug preselected.
          if (c.sourceType === "repo") {
            return (
              <a
                key={`${c.sourceId}-${idx}`}
                href={`/graph?slug=${encodeURIComponent(c.sourceId)}`}
                className="no-underline"
                title={`Open ${c.label} in the service map`}
              >
                <Badge tone={tone}>{c.label}</Badge>
              </a>
            );
          }
          return (
            <Badge key={`${c.sourceId}-${idx}`} tone={tone}>
              {c.label}
            </Badge>
          );
        })}
        {citations.length > CITATION_PREVIEW_COUNT && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-zinc-600 underline-offset-2 hover:underline"
          >
            {expanded ? "Show fewer" : `Show all (${hidden} more)`}
          </button>
        )}
      </div>
    </div>
  );
}

function Turn({
  role,
  content,
  citations,
  isStreaming,
  error,
}: {
  role: "user" | "assistant";
  content: string;
  citations: { sourceType: string; sourceId: string; label: string }[];
  isStreaming?: boolean;
  error?: string | null;
}) {
  if (role === "user") {
    return (
      <div className="flex">
        <div className="ml-auto max-w-[85%] rounded-md bg-zinc-900 px-3 py-2 text-sm text-white">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="mr-auto max-w-[95%]">
      {citations.length > 0 && (
        <CitationStrip citations={citations} />
      )}
      {content && <MarkdownAnswer content={content} />}
      {isStreaming && (
        <span className="ml-1 inline-block h-4 w-1.5 animate-pulse bg-zinc-400 align-middle" />
      )}
      {error && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
    </div>
  );
}
